import { BRAND } from './brand';
import { api } from '@/api/client';

// Cache for settings
let _settingsCache = null;

// Instagram QR code (shown on invoice + RMA prints, right-aligned)
const INSTAGRAM_QR_URL = 'https://customer-assets.emergentagent.com/job_claude-check-7/artifacts/iihq2ovq_WhatsApp%20Image%202026-06-22%20at%201.55.51%20AM.jpeg';
const INSTAGRAM_HANDLE = '@PCNERDSLK';
// Documents that should show the IG QR in the header
const QR_DOC_TYPES = new Set(['invoice', 'rma', 'quotation']);

const getSettings = async () => {
  if (_settingsCache) return _settingsCache;
  try {
    const res = await api.getSettings();
    _settingsCache = res.data;
    return _settingsCache;
  } catch (e) {
    return {
      company_name: BRAND.name,
      tagline: BRAND.tagline,
      phone: '', email: '', address: '', website: '', tax_id: '', currency: 'LKR'
    };
  }
};

// Clear cache (called when settings are updated)
export const clearSettingsCache = () => { _settingsCache = null; };

// Generate print HTML with branded header
export const generatePrintDocument = ({ title, content, footer = '', settings, docType = 'generic' }) => {
  const s = settings || {
    company_name: BRAND.name,
    tagline: BRAND.tagline,
    phone: '', email: '', address: '', website: '', tax_id: '', currency: 'LKR',
    invoice_accent_color: '#111424',
    invoice_show_logo: true,
    invoice_show_tagline: true,
    invoice_show_signatures: true
  };
  // For print, default to the refined dark navy unless user explicitly chose otherwise
  const accent = s.invoice_accent_color && s.invoice_accent_color !== '#6d28d9' ? s.invoice_accent_color : '#111424';
  const showLogo = s.invoice_show_logo !== false;
  const showTagline = s.invoice_show_tagline !== false;
  const contactLine = [s.phone && s.phone, s.email && s.email, s.website && s.website].filter(Boolean).join(' &nbsp;·&nbsp; ');
  const customFooter = docType === 'invoice' ? (s.invoice_footer || '') : '';
  const termsText = docType === 'invoice' ? (s.invoice_terms || '') : '';
  const thankYouMsg = docType === 'invoice' && s.invoice_thank_you_message
    ? s.invoice_thank_you_message
    : `Thank you for choosing ${s.company_name}`;
  
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title} - ${s.company_name}</title>
        <meta charset="UTF-8" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600&family=Manrope:wght@400;500;600&display=swap" rel="stylesheet" />
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif;
            color: #111216;
            padding: 32px;
            background: #fff;
            font-size: 13px;
            line-height: 1.5;
          }
          .doc-header {
            display: flex;
            align-items: center;
            gap: 18px;
            padding-bottom: 18px;
            border-bottom: 1px solid #E4E5E8;
            margin-bottom: 32px;
          }
          .doc-logo {
            width: 64px;
            height: 64px;
            object-fit: contain;
            border-radius: 6px;
          }
          .doc-brand { flex: 1; }
          .doc-instagram {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            text-align: center;
          }
          .doc-instagram img {
            width: 96px;
            height: 96px;
            object-fit: contain;
            border-radius: 8px;
          }
          .doc-instagram .ig-label {
            font-size: 9px;
            color: #686C75;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            font-weight: 600;
          }
          .doc-instagram .ig-handle {
            font-size: 10px;
            color: #E1306C;
            font-weight: 600;
          }
          .doc-brand h1 {
            font-family: 'Outfit', sans-serif;
            font-size: 22px;
            font-weight: 600;
            color: ${accent};
            margin: 0;
            letter-spacing: -0.02em;
          }
          .doc-brand .tagline {
            font-size: 10px;
            color: #686C75;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            margin-top: 3px;
          }
          .doc-brand .contact, .doc-brand .address {
            font-size: 11px;
            color: #686C75;
            margin-top: 4px;
          }
          .doc-title-bar {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 24px;
          }
          .doc-title {
            font-family: 'Outfit', sans-serif;
            font-size: 24px;
            font-weight: 500;
            color: #111216;
            letter-spacing: -0.02em;
          }
          .doc-meta {
            text-align: right;
            font-size: 11px;
            color: #686C75;
            line-height: 1.7;
          }
          .doc-meta strong { color: #111216; font-weight: 600; }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
            margin-bottom: 28px;
          }
          .info-card {
            padding: 14px 16px;
            background: #F8F9FA;
            border: 1px solid #E4E5E8;
            border-left: 2px solid ${accent};
            border-radius: 4px;
          }
          .info-card h3 {
            font-size: 10px;
            color: #686C75;
            text-transform: uppercase;
            margin-bottom: 8px;
            letter-spacing: 0.15em;
            font-weight: 600;
          }
          .info-card p { font-size: 12px; margin: 2px 0; color: #3B3F47; }
          .info-card p strong { color: #111216; }
          .doc-table {
            width: 100%;
            border-collapse: collapse;
            margin: 16px 0;
            font-size: 12px;
          }
          .doc-table th {
            background: transparent;
            color: #686C75;
            padding: 10px 12px;
            text-align: left;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.15em;
            border-bottom: 1px solid #D4D6DA;
          }
          .doc-table td {
            padding: 12px;
            border-bottom: 1px solid #E4E5E8;
            color: #3B3F47;
          }
          .doc-table tr:last-child td { border-bottom: none; }
          .totals {
            margin-left: auto;
            width: 280px;
            margin-top: 16px;
          }
          .totals .row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            font-size: 12px;
            color: #3B3F47;
          }
          .totals .row.total {
            border-top: 2px solid #0f172a;
            padding-top: 10px;
            margin-top: 6px;
            font-size: 17px;
            font-weight: 700;
            color: ${accent};
          }
          .doc-footer {
            margin-top: 40px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            font-size: 11px;
            color: #6b7280;
          }
          .doc-footer strong { color: ${accent}; }
          .terms-box {
            margin-top: 30px;
            padding: 12px 16px;
            background: #fffbeb;
            border-left: 3px solid #f59e0b;
            border-radius: 4px;
            font-size: 11px;
            color: #78350f;
            line-height: 1.5;
          }
          .terms-box h4 {
            font-size: 11px;
            text-transform: uppercase;
            color: #92400e;
            margin-bottom: 6px;
            letter-spacing: 1px;
          }
          .custom-footer {
            margin-top: 16px;
            padding: 12px;
            text-align: center;
            font-size: 11px;
            color: #6b7280;
            font-style: italic;
          }
          .signature-grid {
            margin-top: 50px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 60px;
          }
          .sig-box {
            border-top: 1px solid #1a1a1a;
            padding-top: 6px;
            font-size: 11px;
            color: #6b7280;
            text-align: center;
          }
          @page { size: A4; margin: 12mm; }
          @media print {
            body { padding: 0; font-size: 11.5px; }
            .doc-header { margin-bottom: 18px; padding-bottom: 12px; }
            .doc-title-bar { margin-bottom: 14px; }
            .info-grid { margin-bottom: 18px; gap: 10px; }
            .doc-table { margin: 8px 0; font-size: 11px; }
            .doc-table th, .doc-table td { padding: 6px 8px; }
            .totals { width: 260px; }
            .doc-footer { margin-top: 24px; }
            .signature-grid { margin-top: 30px; }
            .page-break-before { page-break-before: always; }
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div class="doc-header">
          ${showLogo ? `<img src="${BRAND.logo}" alt="${s.company_name}" class="doc-logo" crossorigin="anonymous" />` : ''}
          <div class="doc-brand">
            <h1>${s.company_name}</h1>
            ${showTagline && s.tagline ? `<div class="tagline">${s.tagline}</div>` : ''}
            ${contactLine ? `<div class="contact">${contactLine}</div>` : ''}
            ${s.address ? `<div class="address">${s.address}</div>` : ''}
            ${s.tax_id ? `<div class="address">Tax ID: ${s.tax_id}</div>` : ''}
          </div>
          ${QR_DOC_TYPES.has(docType) ? `
            <div class="doc-instagram">
              <img src="${INSTAGRAM_QR_URL}" alt="Instagram @PCNERDSLK QR" crossorigin="anonymous" />
              <div class="ig-label">Follow us</div>
              <div class="ig-handle">${INSTAGRAM_HANDLE}</div>
            </div>
          ` : ''}
        </div>
        ${content}
        ${termsText ? `<div class="terms-box"><h4>Terms & Conditions</h4>${termsText.replace(/\n/g, '<br/>')}</div>` : ''}
        ${customFooter ? `<div class="custom-footer">${customFooter}</div>` : ''}
        <div class="doc-footer">
          ${thankYouMsg}
          ${footer ? `<br/>${footer}` : ''}
        </div>
      </body>
    </html>
  `;
};

// Open print window with content
export const printDocument = async ({ title, content, footer, docType = 'generic' }) => {
  const settings = await getSettings();
  const html = generatePrintDocument({ title, content, footer, settings, docType });
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    alert('Please allow popups to print documents');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    setTimeout(() => {
      win.print();
    }, 500);
  };
};

// Get the settings synchronously from cache (for content builders)
const getCachedSettings = () => _settingsCache;

// Currency formatter matching useCurrency.formatCurrency
const fmtCur = (amount, currency = 'LKR') => {
  const num = Number(amount || 0);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
  const isSymbol = /^[^\w]/.test(String(currency).trim());
  return isSymbol ? `${currency}${formatted}` : `${currency} ${formatted}`;
};

// Invoice print content
export const buildInvoiceContent = ({ invoice, customer, items = [] }) => {
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const discount = invoice.discount || 0;
  const total = subtotal - discount;
  const balance = total - (invoice.paid_amount || 0);
  const s = getCachedSettings() || {};
  const docTitle = s.invoice_title || 'Invoice';
  const showSigs = s.invoice_show_signatures !== false;
  const currency = s.currency || 'LKR';

  // Format warranty months → readable text
  const fmtWarranty = (m) => {
    const months = parseInt(m) || 0;
    if (months <= 0) return '';
    if (months === 12) return '1 Year';
    if (months === 24) return '2 Years';
    if (months === 36) return '3 Years';
    if (months % 12 === 0) return `${months / 12} Years`;
    return `${months} mo`;
  };
  const invoiceDate = new Date(invoice.created_at);
  const addMonths = (d, m) => { const nd = new Date(d); nd.setMonth(nd.getMonth() + (parseInt(m) || 0)); return nd; };
  const fmtShortDate = (d) => d instanceof Date && !isNaN(d) ? d.toLocaleDateString('en-GB') : '';

  // Terms & conditions for warranty (printed on back page — all 8 sections must fit on one A4 page)
  const defaultWarrantyTerms = `1. WARRANTY PERIOD
The warranty period will be as indicated against each item.
   • 1 Year = 365 Days Only  •  2 Years = 730 Days  •  3 Years = 1,095 Days  •  N/W = No Warranty
Warranty is provided by the respective manufacturer / supplier unless specified by the seller, as per their service policy.

2. WARRANTY EXCLUSION
Warranty is NOT applicable for physical customer-induced damage: LCD burn marks, pin damage, corrosion or oxidation, scratches, lightning or voltage fluctuations, food or beverage spill, damage by animals or insects, unauthorized repair / tampering / modification, force majeure events (fire, flood, earthquake, etc.), Cables / Accessories (no warranty), backlight bleed on monitors / laptops (not covered). Unauthorized repairs will strictly void the warranty.

3. ACCESSORIES WARRANTY
Warranty for accessories (batteries, adapters, cables) may differ from the core product, as determined by the manufacturer.

4. WARRANTY COVERAGE
   • Covers manufacturer defects only.
   • Laptop carry-in repair warranty only (no replacement).
   • Laptop battery and adapter carry 1-year warranty from the manufacturer.
   • Pixel policy for monitors / laptops applies as per manufacturer guidelines.

5. PROOF OF PURCHASE & PRODUCT IDENTIFICATION
The original invoice must be produced for all warranty claims. Warranty is valid only if the product serial number and warranty stickers are intact and clearly identifiable. Missing or erased serial numbers will render the warranty VOID.

6. DATA RESPONSIBILITY
PC NERDS Store is not responsible for any data loss, revenue loss or business disruption caused by hardware failures, repairs, replacement, virus or malware. Customers are strongly advised to back up all data before submitting items for service.

7. SERVICE TYPE
Warranty applies for CARRY-IN service only. All items must be brought in to PC NERDS Store for warranty claims.

8. RETURN POLICY
No returns are accepted after 7 days. Returned items must be in brand-new condition with original packaging and stickers intact. Refunds are issued after store inspection. All return decisions by the store are final and non-negotiable.`;
  const warrantyTerms = (s.warranty_terms && s.warranty_terms.trim()) ? s.warranty_terms : defaultWarrantyTerms;

  return `
    <div class="doc-title-bar">
      <div class="doc-title">${docTitle}</div>
      <div class="doc-meta">
        <div><strong>Invoice ID:</strong> ${invoice.id.slice(0, 12).toUpperCase()}</div>
        <div><strong>Date:</strong> ${new Date(invoice.created_at).toLocaleDateString()}</div>
        <div><strong>Status:</strong> ${invoice.status}</div>
      </div>
    </div>
    <div class="info-grid">
      <div class="info-card">
        <h3>Bill To</h3>
        <p><strong>${customer?.name || 'Walk-in Customer'}</strong></p>
        ${customer?.phone ? `<p>${customer.phone}</p>` : ''}
        ${customer?.email ? `<p>${customer.email}</p>` : ''}
        ${customer?.address ? `<p>${customer.address}</p>` : ''}
      </div>
      <div class="info-card">
        <h3>Payment &amp; Sales</h3>
        <p><strong>Payment Method:</strong> ${invoice.payment_type || invoice.payment_method || 'Cash'}</p>
        <p><strong>Paid:</strong> ${fmtCur(invoice.paid_amount || 0, currency)}</p>
        <p><strong>Balance:</strong> ${fmtCur(balance, currency)}</p>
        ${invoice.due_date ? `<p><strong>Due Date:</strong> ${invoice.due_date}</p>` : ''}
        ${invoice.created_by_name ? `<p><strong>Sales Person:</strong> ${invoice.created_by_name}</p>` : ''}
      </div>
    </div>
    <table class="doc-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Product</th>
          <th>Qty</th>
          <th>Warranty</th>
          <th>Price</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        ${(() => {
          const mode = s.invoice_serial_display || 'Grouped';
          const warrantyLabel = (warrantyMonths) => {
            const w = fmtWarranty(warrantyMonths);
            if (!w) return '';
            const expiry = addMonths(invoiceDate, warrantyMonths);
            return `<div style="font-weight:600">${w}</div><div style="font-size:10px;color:#686C75">exp. ${fmtShortDate(expiry)}</div>`;
          };
          if (mode === 'Individual') {
            return items.map((item, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>
                  <div>${item.product_name}</div>
                  ${item.serial ? `<div style="font-size:10px;color:#686C75;font-family:monospace;margin-top:2px">SN: ${item.serial}</div>` : ''}
                </td>
                <td>${item.qty}</td>
                <td>${warrantyLabel(item.warranty)}</td>
                <td>${fmtCur(item.price, currency)}</td>
                <td>${fmtCur(item.qty * item.price, currency)}</td>
              </tr>
            `).join('');
          }
          // Grouped mode: collapse identical (product_id + price + warranty) into one row
          const groups = [];
          const keyMap = new Map();
          items.forEach((it) => {
            const key = `${it.product_id || it.product_name}|${it.price}|${it.warranty || ''}`;
            if (keyMap.has(key)) {
              const g = keyMap.get(key);
              g.qty += (it.qty || 1);
              if (it.serial) g.serials.push(it.serial);
            } else {
              const g = {
                product_id: it.product_id,
                product_name: it.product_name,
                price: it.price,
                warranty: it.warranty || '',
                qty: it.qty || 1,
                serials: it.serial ? [it.serial] : []
              };
              keyMap.set(key, g);
              groups.push(g);
            }
          });
          return groups.map((g, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>
                <div style="font-weight:600">${g.product_name}</div>
                ${g.serials.length ? `<div style="font-size:10px;color:#3B3F47;margin-top:4px"><strong>SN${g.serials.length > 1 ? 's' : ''}:</strong> <span style="font-family:monospace">${g.serials.join(', ')}</span></div>` : ''}
              </td>
              <td>${g.qty}</td>
              <td>${warrantyLabel(g.warranty)}</td>
              <td>${fmtCur(g.price, currency)}</td>
              <td>${fmtCur(g.qty * g.price, currency)}</td>
            </tr>
          `).join('');
        })()}
      </tbody>
    </table>
    <div class="totals">
      <div class="row"><span>Subtotal:</span><span>${fmtCur(subtotal, currency)}</span></div>
      <div class="row"><span>Discount:</span><span>-${fmtCur(discount, currency)}</span></div>
      <div class="row total"><span>Total:</span><span>${fmtCur(total, currency)}</span></div>
    </div>
    ${invoice.notes ? `<div class="info-card" style="margin-top:20px"><h3>Notes</h3><p>${invoice.notes}</p></div>` : ''}
    ${showSigs ? `<div class="signature-grid">
      <div class="sig-box">Customer Signature</div>
      <div class="sig-box">Authorized Signature</div>
    </div>` : ''}

    <!-- ================== BACK OF PAGE: Warranty Terms ================== -->
    <div class="page-break-before" style="padding-top:0">
      <div style="text-align:center;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #111424">
        <h2 style="font-family:'Outfit',sans-serif;font-size:16px;color:#111424;letter-spacing:-0.02em;margin:0">Warranty Terms &amp; Conditions</h2>
        <div style="font-size:9.5px;color:#686C75;margin-top:2px">Invoice ${invoice.id.slice(0, 12).toUpperCase()} · Issued ${new Date(invoice.created_at).toLocaleDateString()}</div>
      </div>
      <div style="font-size:9.5px;line-height:1.4;color:#1A1D24;white-space:pre-line;column-count:2;column-gap:14px;column-rule:1px solid #E5E7EB">${warrantyTerms}</div>

      <div style="margin-top:10px;padding:6px 10px;background:#F8F9FA;border-left:3px solid #111424;border-radius:3px;font-size:9.5px;line-height:1.4">
        <strong>Warranty starts on:</strong> ${new Date(invoice.created_at).toLocaleDateString()} &nbsp;·&nbsp;
        <strong>For claims:</strong> bring this invoice and the item with its original serial number(s) to PC NERDS.
      </div>

      <div style="margin-top:10px;text-align:center;font-size:9px;color:#686C75;letter-spacing:0.15em;text-transform:uppercase">
        Thank you for choosing ${s.company_name || 'PC NERDS'} &nbsp;·&nbsp; ${s.phone || ''} &nbsp;·&nbsp; ${s.email || ''}
      </div>
    </div>
  `;
};

// RMA print content
export const buildRMAContent = ({ rma, customer, product }) => {
  const pi = rma.purchase_info || {};
  const hasReplacement = !!(rma.serial_replacement || rma.replacement_product_name);
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '';
  return `
    <div class="doc-title-bar">
      <div class="doc-title">RMA Job Note</div>
      <div class="doc-meta">
        <div><strong>Job ID:</strong> ${rma.job_id || rma.id.slice(0, 12).toUpperCase()}</div>
        <div><strong>Date Created:</strong> ${fmtDateTime(rma.created_at)}</div>
        <div><strong>Last Updated:</strong> ${fmtDateTime(rma.updated_at)}</div>
        <div><strong>Status:</strong> ${rma.status}</div>
      </div>
    </div>
    <div class="info-grid">
      <div class="info-card">
        <h3>Customer</h3>
        <p><strong>${customer?.name || pi.customer_name || 'Unknown'}</strong></p>
        ${(customer?.phone || pi.customer_phone) ? `<p>${customer?.phone || pi.customer_phone}</p>` : ''}
        ${customer?.email ? `<p>${customer.email}</p>` : ''}
      </div>
      <div class="info-card">
        <h3>Purchase Information</h3>
        ${pi.invoice_number ? `<p><strong>Invoice #:</strong> ${pi.invoice_number}</p>` : ''}
        ${pi.invoice_date ? `<p><strong>Invoice Date:</strong> ${pi.invoice_date}</p>` : ''}
        ${pi.purchase_date && pi.purchase_date !== pi.invoice_date ? `<p><strong>Purchase Date:</strong> ${pi.purchase_date}</p>` : ''}
        ${pi.warranty_months ? `<p><strong>Warranty:</strong> ${pi.warranty_months} months</p>` : ''}
      </div>
    </div>
    <div class="info-grid">
      <div class="info-card">
        <h3>Original Item</h3>
        <p><strong>${product?.name || pi.product_name || 'N/A'}</strong></p>
        ${(product?.brand || pi.brand) ? `<p>Brand: ${product?.brand || pi.brand}</p>` : ''}
        ${(product?.model || pi.model) ? `<p>Model: ${product?.model || pi.model}</p>` : ''}
        <p>Original Serial: <strong>${rma.serial_original}</strong></p>
      </div>
      ${hasReplacement ? `
      <div class="info-card">
        <h3>Replacement Item</h3>
        ${rma.replacement_product_name ? `<p><strong>${rma.replacement_product_name}</strong></p>` : ''}
        ${rma.replacement_brand ? `<p>Brand: ${rma.replacement_brand}</p>` : ''}
        ${rma.replacement_model ? `<p>Model: ${rma.replacement_model}</p>` : ''}
        ${rma.serial_replacement ? `<p>Replacement Serial: <strong>${rma.serial_replacement}</strong></p>` : ''}
      </div>
      ` : ''}
    </div>
    <div class="info-grid">
      <div class="info-card">
        <h3>Job Note Prepared By</h3>
        <p><strong>${rma.created_by_name || '—'}</strong></p>
        <p>Tested By: <strong>${rma.tested_by_name || '—'}</strong>${rma.test_date ? ` (on ${rma.test_date})` : ''}</p>
        ${rma.diagnosis_notes ? `<p style="margin-top:8px"><strong>Diagnosis:</strong> ${rma.diagnosis_notes}</p>` : ''}
      </div>
      <div class="info-card">
        <h3>Other Notes</h3>
        ${rma.tech_notes ? `<p><strong>Tech Notes:</strong> ${rma.tech_notes}</p>` : ''}
        ${rma.standby_serial ? `<p><strong>Standby Loaned:</strong> ${rma.standby_product_name || ''}${rma.standby_product_name ? '<br>' : ''}SN: ${rma.standby_serial} ${rma.standby_returned ? '(Returned)' : '(Loaned)'}</p>` : ''}
        ${rma.supplier_rma ? '<p><strong>Supplier RMA:</strong> Yes — Escalated to supplier</p>' : ''}
      </div>
    </div>
    <table class="doc-table">
      <thead>
        <tr><th>Issue Reported</th></tr>
      </thead>
      <tbody>
        <tr><td>${rma.issue}</td></tr>
      </tbody>
    </table>
    <div class="signature-grid">
      <div class="sig-box">Customer Signature</div>
      <div class="sig-box">Technician Signature</div>
    </div>
  `;
};

// Repair Receipt print content
export const buildRepairContent = ({ repair, customer }) => {
  const s = getCachedSettings() || {};
  const currency = s.currency || 'LKR';
  return `
    <div class="doc-title-bar">
      <div class="doc-title">Repair Receipt</div>
      <div class="doc-meta">
        <div><strong>Repair ID:</strong> ${repair.id.slice(0, 12).toUpperCase()}</div>
        <div><strong>Date:</strong> ${new Date(repair.created_at).toLocaleDateString()}</div>
        <div><strong>Status:</strong> ${repair.status}</div>
      </div>
    </div>
    <div class="info-grid">
      <div class="info-card">
        <h3>Customer</h3>
        <p><strong>${customer?.name || 'Unknown'}</strong></p>
        ${customer?.phone ? `<p>${customer.phone}</p>` : ''}
        ${customer?.email ? `<p>${customer.email}</p>` : ''}
      </div>
      <div class="info-card">
        <h3>Repair Info</h3>
        <p><strong>Device:</strong> ${repair.device}</p>
        <p><strong>Cost:</strong> ${fmtCur(repair.cost || 0, currency)}</p>
        <p><strong>Payment:</strong> ${repair.paid ? 'Paid' : 'Unpaid'}</p>
      </div>
    </div>
    <table class="doc-table">
      <thead><tr><th>Field</th><th>Details</th></tr></thead>
      <tbody>
        <tr><td><strong>Issue Reported</strong></td><td>${repair.issue}</td></tr>
        ${repair.tech_notes ? `<tr><td><strong>Tech Notes</strong></td><td>${repair.tech_notes}</td></tr>` : ''}
      </tbody>
    </table>
    <div class="signature-grid">
      <div class="sig-box">Customer Signature</div>
      <div class="sig-box">Technician Signature</div>
    </div>
  `;
};

// GRN print content
export const buildGRNContent = ({ grn }) => {
  const total = (grn.items || []).reduce((sum, item) => sum + (item.cost * item.qty), 0);
  const s = getCachedSettings() || {};
  const currency = s.currency || 'LKR';
  return `
    <div class="doc-title-bar">
      <div class="doc-title">Goods Received Note</div>
      <div class="doc-meta">
        <div><strong>GRN No:</strong> ${grn.grn_number || grn.id.slice(0, 12).toUpperCase()}</div>
        <div><strong>Date:</strong> ${grn.received_date}</div>
        <div><strong>Status:</strong> ${grn.payment_status}</div>
      </div>
    </div>
    <div class="info-grid">
      <div class="info-card">
        <h3>Supplier</h3>
        <p><strong>${grn.supplier_name}</strong></p>
        <p>Supplier Invoice: ${grn.invoice_number || '<em>No Supplier Invoice</em>'}</p>
      </div>
      <div class="info-card">
        <h3>Payment</h3>
        <p><strong>Status:</strong> ${grn.payment_status}</p>
        ${grn.due_date ? `<p><strong>Due Date:</strong> ${grn.due_date}</p>` : ''}
      </div>
    </div>
    <table class="doc-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Product</th>
          <th>Qty</th>
          <th>Cost</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        ${(grn.items || []).map((item, i) => {
          const serials = Array.isArray(item.serials) ? item.serials.filter(Boolean) : [];
          const serialRow = serials.length ? `
            <tr class="grn-serials">
              <td></td>
              <td colspan="4" style="padding-left:12px;font-size:11px;color:#444;">
                <strong style="color:#222">Serial Number${serials.length > 1 ? 's' : ''} (${serials.length}):</strong>
                <span style="font-family:'Courier New', monospace; word-break: break-all;">
                  ${serials.join(', ')}
                </span>
              </td>
            </tr>
          ` : '';
          return `
            <tr>
              <td>${i + 1}</td>
              <td>${item.product_name}</td>
              <td>${item.qty}</td>
              <td>${fmtCur(item.cost, currency)}</td>
              <td>${fmtCur(item.cost * item.qty, currency)}</td>
            </tr>
            ${serialRow}
          `;
        }).join('')}
      </tbody>
    </table>
    <div class="totals">
      <div class="row total"><span>Total Value:</span><span>${fmtCur(total, currency)}</span></div>
    </div>
  `;
};
